// CODA-1008: Neukundenonboarding – list page with wizard trigger.
import { useState, useEffect, useCallback } from 'react'
import { isAxiosError } from 'axios'
import apiClient from '@/lib/api'
import {
  PageHeader,
  Button,
  DataTable,
  StatusBadge,
  EmptyState,
  LoadingState,
  ErrorState,
  useToast,
} from '@/components/ui'
import { UserCheck, Plus, RefreshCw } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { OnboardingWizardModal } from './OnboardingWizardModal'
import { OnboardingDetailModal } from './OnboardingDetailModal'

interface OnboardingRequest {
  id: string
  status: 'draft' | 'sent' | 'in_progress' | 'completed' | 'expired'
  company_name: string
  contact_email: string
  contact_first_name: string | null
  contact_last_name: string | null
  sap_card_code: string | null
  sap_sync_status: 'pending' | 'success' | 'error'
  created_at: string
  completed_at: string | null
}

const STATUS_LABEL: Record<OnboardingRequest['status'], string> = {
  draft: 'Entwurf',
  sent: 'Versendet',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
  expired: 'Abgelaufen',
}

const columns: ColumnDef<OnboardingRequest>[] = [
  {
    accessorKey: 'company_name',
    header: 'Firma',
  },
  {
    accessorKey: 'contact_email',
    header: 'E-Mail',
  },
  {
    id: 'contact',
    header: 'Ansprechpartner',
    cell: ({ row }) => {
      const r = row.original
      const name = [r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ')
      return name || '–'
    },
  },
  {
    accessorKey: 'sap_card_code',
    header: 'SAP-CardCode',
    cell: ({ getValue }) => (getValue() as string | null) ?? '–',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const s = getValue() as OnboardingRequest['status']
      return <StatusBadge status={s} label={STATUS_LABEL[s]} />
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Erstellt am',
    cell: ({ getValue }) =>
      new Date(getValue() as string).toLocaleDateString('de-DE'),
  },
]

export default function OnboardingPage() {
  const { toast } = useToast()
  const [requests, setRequests] = useState<OnboardingRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.get<OnboardingRequest[]>('/onboarding')
      setRequests(Array.isArray(data) ? data : [])
    } catch (err) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.message)
        : 'Unbekannter Fehler'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  function handleWizardComplete() {
    setWizardOpen(false)
    toast({ title: 'Onboarding gestartet', description: 'Der Kunde wurde angelegt und der Link wurde versendet.' })
    load()
  }

  return (
    <>
      <PageHeader
        title="Neukundenonboarding"
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={load}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="primary" size="sm" onClick={() => setWizardOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Neues Onboarding
            </Button>
          </div>
        }
      />

      {loading && <LoadingState label="Lade Onboarding-Vorgänge…" />}

      {!loading && error && (
        <ErrorState
          title="Fehler beim Laden"
          description={error}
          onRetry={load}
        />
      )}

      {!loading && !error && requests.length === 0 && (
        <EmptyState
          icon={<UserCheck />}
          title="Noch keine Onboardings"
          description="Starte ein neues Onboarding, um einen Kunden anzulegen."
          action={
            <Button variant="primary" onClick={() => setWizardOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Neues Onboarding
            </Button>
          }
        />
      )}

      {!loading && !error && requests.length > 0 && (
        <DataTable
          columns={columns}
          data={requests}
          onRowClick={(row) => setSelectedId(row.id)}
        />
      )}

      {wizardOpen && (
        <OnboardingWizardModal
          onClose={() => setWizardOpen(false)}
          onComplete={handleWizardComplete}
        />
      )}

      {selectedId && (
        <OnboardingDetailModal
          requestId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={load}
        />
      )}
    </>
  )
}
